import type { ExpressionSpecification, FilterSpecification, SymbolLayerSpecification } from 'mapbox-gl';
import { Layer } from 'react-map-gl/mapbox';
import { COLOR_NEUTRAL_WHITE, COLOR_TEXT_PRIMARY, COLOR_TEXT_SECONDARY } from '@alltrails/denali/tokens';
import useMap from '../../hooks/useMap';
import {
  buildTrailheadCollapsedIconImage,
  buildTrailheadNameTextField,
  buildTrailheadSecondaryIconImage,
  buildTrailheadSortKey,
  secondaryTrailheadIconSize,
  trailheadIconVerticalOffset,
  type TrailheadVariantExpressions
} from '../../utils/buildTrailheadIconExpressions';
import {
  getLayerBeforeId,
  trailheadsOverlayActiveNameId,
  trailheadsOverlayCollapsedActiveId,
  trailheadsOverlayCollapsedId,
  trailheadsOverlayLayerId,
  trailheadsOverlayNamesId,
  trailheadsOverlaySecondaryId
} from '../../utils/layers';
import { getTrailheadPinStyle, type TrailheadsExploreVariant } from '../../utils/trailheadPinVariant';

// https://www.figma.com/design/Hfw8SV8IylHR4A3nnQ7Fvs/Map-Icons?node-id=481-1698&p=f&m=dev

// From here on the map is zoomed in far enough that no pin is collapsed (see pinZoomPopularityMap), so every pin shows its name.
const trailheadNamesAlwaysShownFromZoom = 15;
// How faint the pins that `isDimmedExpression` matches are drawn.
const dimmedPinOpacity = 0.7;

/** What a pin layer component takes from the overlay, whichever way it draws the expanded pins. */
export type TrailheadPinLayerProps = {
  activeNameLayerId?: string;
  collapsedActiveLayerId?: string;
  collapsedLayerId?: string;
  filter?: ExpressionSpecification;
  isCollapsedExpression: ExpressionSpecification;
  /** True for the trailheads drawn faintly, icon and text, such as the other pins on a trailhead page. Nothing is dimmed without it. */
  isDimmedExpression?: ExpressionSpecification;
  isHoveredExpression: ExpressionSpecification;
  /** True for the trailheads with no trail in the filtered results. They draw as the secondary pin and take no part in the other layers. */
  isSecondaryExpression?: ExpressionSpecification;
  layerId?: string;
  nameLayerId?: string;
  pinVariant?: TrailheadsExploreVariant;
  popularityExpression?: ExpressionSpecification | number;
  secondaryLayerId?: string;
  sourceId: string;
  sourceLayer?: string;
} & TrailheadVariantExpressions;

type TrailheadPinLayersProps = Omit<TrailheadPinLayerProps, 'isHoveredExpression'> & {
  /** Text color of an expanded pin that is hovered or selected, when it differs from the resting color. */
  activeTextColor?: string;
  /** Icon and text of a collapsed pin that expands on hover or select. */
  collapsedActiveLayout: SymbolLayerSpecification['layout'];
  /** Icon and text of an expanded pin. */
  expandedLayout: SymbolLayerSpecification['layout'];
  isActiveExpression: ExpressionSpecification;
  nameTextOffset: ExpressionSpecification;
};

/**
 * The layers every trailhead pin style draws: names, expanded pins, collapsed dots, and the hovered or selected pins.
 * A style only says how its expanded and active pins look.
 */
const TrailheadPinLayers = ({
  activeNameLayerId = trailheadsOverlayActiveNameId,
  activeTextColor,
  collapsedActiveLayerId = trailheadsOverlayCollapsedActiveId,
  collapsedActiveLayout,
  collapsedLayerId = trailheadsOverlayCollapsedId,
  expandedLayout,
  filter,
  isActiveExpression,
  isClosedExpression,
  isCollapsedExpression,
  isCompletedExpression,
  isDimmedExpression,
  isSavedExpression,
  isSecondaryExpression,
  layerId = trailheadsOverlayLayerId,
  nameLayerId = trailheadsOverlayNamesId,
  nameTextOffset,
  pinVariant,
  popularityExpression = 0,
  secondaryLayerId = trailheadsOverlaySecondaryId,
  sourceId,
  sourceLayer
}: TrailheadPinLayersProps) => {
  const map = useMap();
  // Secondary trailheads keep out of every layer but their own, so the layers below read their conditions as primary only.
  const primaryOnly: ExpressionSpecification[] = isSecondaryExpression ? [['!', isSecondaryExpression]] : [];
  const variantExpressions = { isClosedExpression, isCompletedExpression, isSavedExpression };
  const textColor: ExpressionSpecification = ['case', isClosedExpression, COLOR_TEXT_SECONDARY, COLOR_TEXT_PRIMARY];
  const opacityPaint: SymbolLayerSpecification['paint'] = isDimmedExpression
    ? {
        'icon-opacity': ['case', isDimmedExpression, dimmedPinOpacity, 1],
        'text-opacity': ['case', isDimmedExpression, dimmedPinOpacity, 1]
      }
    : {};
  const pinPaint: SymbolLayerSpecification['paint'] = {
    ...opacityPaint,
    'text-color': activeTextColor === undefined ? textColor : ['case', isActiveExpression, activeTextColor, textColor]
  };
  const baseLayout: SymbolLayerSpecification['layout'] = {
    'icon-allow-overlap': true,
    'icon-ignore-placement': true,
    'text-allow-overlap': true,
    'text-ignore-placement': true,
    'text-font': ['AllTrails Aeonik Medium', 'Manrope Bold'],
    'text-line-height': 1,
    'symbol-sort-key': buildTrailheadSortKey(isActiveExpression, popularityExpression, variantExpressions),
    'icon-size': ['interpolate', ['linear'], ['zoom'], 10, 0.7, 14, 1.0],
    'text-size': ['interpolate', ['linear'], ['zoom'], 10, 11, 14, 12]
  };
  const nameLayout: SymbolLayerSpecification['layout'] = { ...baseLayout, 'text-offset': nameTextOffset };
  const namePaint: SymbolLayerSpecification['paint'] = {
    ...opacityPaint,
    'text-color': textColor,
    'text-halo-color': COLOR_NEUTRAL_WHITE,
    'text-halo-width': 1.5
  };

  // Cluster tiles come with their own `point_count`, which these pins never draw, since a trailhead
  // already counts its trails.
  const symbolLayer = (
    id: string,
    conditions: ExpressionSpecification[],
    layout: SymbolLayerSpecification['layout'],
    paint?: SymbolLayerSpecification['paint']
  ) => (
    <Layer
      beforeId={getLayerBeforeId(map, id)}
      filter={['all', ['!', ['has', 'point_count']], ...primaryOnly, ...conditions, ...(filter === undefined ? [] : [filter])] as FilterSpecification}
      id={id}
      layout={layout}
      paint={paint}
      source={sourceId}
      {...(sourceLayer === undefined ? {} : { 'source-layer': sourceLayer })}
      type="symbol"
    />
  );

  // The secondary pin is the same default circle for every pin style: no count, no label, and no name.
  const secondaryLayer = isSecondaryExpression ? (
    <Layer
      beforeId={getLayerBeforeId(map, secondaryLayerId)}
      filter={['all', ['!', ['has', 'point_count']], isSecondaryExpression, ...(filter === undefined ? [] : [filter])] as FilterSpecification}
      id={secondaryLayerId}
      layout={{
        // Secondary pins hide when they collide with each other. `icon-ignore-placement` has to be false too, or they are never
        // added to the collision index for their neighbors to check against.
        'icon-allow-overlap': false,
        'icon-ignore-placement': false,
        'icon-image': buildTrailheadSecondaryIconImage(getTrailheadPinStyle(pinVariant)),
        'icon-offset': [0, trailheadIconVerticalOffset],
        'icon-size': secondaryTrailheadIconSize,
        'symbol-sort-key': buildTrailheadSortKey(['literal', false], popularityExpression)
      }}
      paint={opacityPaint}
      source={sourceId}
      {...(sourceLayer === undefined ? {} : { 'source-layer': sourceLayer })}
      type="symbol"
    />
  ) : null;

  // The layers are added in order: when none of them are on the map yet, they all resolve the same `beforeId`,
  // and a layer added later lands above one added earlier.
  return (
    <>
      {/* Secondary pins, under every other trailhead layer. */}
      {secondaryLayer}
      {/* Names, below the pins so a pin covers its neighbors' names. The hovered or selected pin's name is written by
          the layer above the pins instead, so none of them can cover it. */}
      {symbolLayer(
        nameLayerId,
        [['!', isActiveExpression]],
        { ...nameLayout, 'text-field': buildTrailheadNameTextField(isActiveExpression, isCollapsedExpression, trailheadNamesAlwaysShownFromZoom) },
        namePaint
      )}
      {/* Expanded pins */}
      {symbolLayer(layerId, [['!', isCollapsedExpression]], { ...baseLayout, ...expandedLayout }, pinPaint)}
      {/* Collapsed pins */}
      {symbolLayer(
        collapsedLayerId,
        [isCollapsedExpression, ['!', isActiveExpression]],
        { ...baseLayout, 'icon-image': buildTrailheadCollapsedIconImage(variantExpressions), 'text-field': '' },
        pinPaint
      )}
      {/* A collapsed pin expands to the active pin on its own layer so it draws over its neighbors. */}
      {symbolLayer(
        collapsedActiveLayerId,
        [isCollapsedExpression, isActiveExpression],
        { ...baseLayout, 'text-field': '', ...collapsedActiveLayout },
        pinPaint
      )}
      {/* The hovered or selected pin's name, collapsed or not, above every pin. */}
      {symbolLayer(activeNameLayerId, [isActiveExpression], { ...nameLayout, 'text-field': ['get', 'name'] }, namePaint)}
    </>
  );
};

export default TrailheadPinLayers;
